// Typed client for the per-copy collection API (mtg-api
// docs/superpowers/specs/2026-10-15-collection-copies-api.md; wire fixtures
// copied into src/api/__fixtures__/copies_wire/).
//
// Rules this module owns (plan 2026-10-16, sections 0, 8 and 9):
//  - EVERY call, reads included, carries the owner's bearer (copies carry
//    purchase prices), through writeAuthFor: identity-bound (refuses when the
//    live session is not the expected user) and API-origin only.
//  - 401: refresh the session once and retry; a second 401 is `unauthorized`.
//  - 429 / 503: retry with the SAME captured token, jittered exponential
//    backoff honoring Retry-After, cancellation-aware, at most 3 retries.
//  - `detail` is parsed as a string OR an object {code|key, message}.
//  - The C1 matrix (with R2-1): a 404 whose detail is `copy_not_found` is an
//    ITEM error; any other 404 on any per-copy call is the collection-level
//    `not_found` and downgrades the capability (copiesCapability.ts). Every
//    outcome is reported there.
//  - Decimal strings (purchase_price) stay strings; nothing here parses them
//    into floats.
import { apiFetch } from '../i18n/apiFetch'
import { apiUrl } from './apiUrl'
import { refreshSessionIdentity, writeAuthFor } from '../lib/accessToken'
import { confirmPerCopy, downgrade, getCapability, isDowngraded, markPerCopyIfUnchanged } from '../lib/copiesCapability'
import { getSupabaseIdentity } from '../hooks/useSupabaseToken'
import { collectionOwnerOf } from '../lib/collectionOwner'
import { noteCollectionWrite } from '../lib/offline/collectionSignal'
import {
  ARRAY_FILTER_KEYS, CAPABILITY, CLIENT_CAPS, COLOR_CODES, COLOR_GROUP_KEYS, COLOR_MODE, COPY_CONDITIONS, DETAIL, ERR, GROUP_KIND, GROUP_MODE, GROUPS, HTTP, SEG,
  TYPE_CATEGORIES, type CopiesInclude, type SectionBy,
  type AddSource, type BulkAction, type Capability, type ColorMode, type CopiesErrorKind, type CopyCondition,
  type CopyFinish, type CopySort, type CopyStatus, type ExportFormat, type GroupKind, type GroupMode, type ItemErrorCode, type SkipReason,
  type SortDir, type ValueRange, MATCH_ROW_KIND, MATCH_STATUSES, type MatchStatus, type TakeAction,
} from './collectionCopies.codes'

// --- Wire types ---------------------------------------------------------------

export interface CopyItem {
  copy_id: number
  card_name: string
  name_key: string
  scryfall_id: string | null
  set: string | null
  set_name: string | null
  collector_number: string | null
  rarity: string | null
  finish: CopyFinish
  condition: CopyCondition | null
  language: string | null
  status: CopyStatus
  qty: number
  /** Exact decimal string, e.g. "2.50". Never parsed to a float for storage. */
  purchase_price: string | null
  purchase_currency: string | null
  source: string
  added_at: string
  unit_price_usd: number | null
  image_normal: string | null
  /** Card fields from the bulk (plan 2026-10-18 A6); null when unknown, absent from older servers. */
  type_line?: string | null
  colors?: string[] | null
  color_identity?: string[] | null
  mv?: number | null
  /** The manual binder holding this line; null = Unsorted (binders spec 2.4). */
  binder_id: number | null
  /**
   * The market change over 7 days in percentage POINTS (6.5 = "+6.5%"),
   * against the history point of `price_change_base_day` (collection views
   * API, 2026-10-27). Absent from older servers; absent or null = unknown.
   */
  price_change_7d_pct?: number | null
  /** The UTC day the 7D change is measured from (YYYY-MM-DD). */
  price_change_base_day?: string | null
  /**
   * Dated daily prices for the last 7 days, the final point today's shown
   * price (`include: ["spark"]` only; never in the snapshot). A null usd is
   * a gap. Absent = no sparkline.
   */
  spark_7d?: SparkPoint[] | null
}

/** One sparkline point: a UTC day and its price (null: no quote that day). */
export interface SparkPoint {
  d: string
  usd: number | null
}

export interface CopiesSummary {
  owned: number
  trade: number
  proxy: number
  want: number
  size_cards: number
  size_unique: number
  last_changed_at: string | null
  /** FIRST page only (absent on later pages). Null = no want lines. */
  want_value_usd?: number | null
  /** FIRST page only. */
  want_unpriced?: number
}

/** One facet bucket: a value (or a set code with its name) and the summed qty. */
export interface FacetCount {
  value: string
  count: number
}

export interface SetFacet {
  code: string
  name: string | null
  count: number
}

/** `facets: true` (plan 2026-10-18 R10): over the FILTERED lines, weighted by qty. */
export interface CopiesFacets {
  sets: SetFacet[]
  rarities: FacetCount[]
  finishes: FacetCount[]
  types: FacetCount[]
  subtypes_top: FacetCount[]
}

export interface CopiesPage {
  items: CopyItem[]
  next_cursor: string | null
  total: number
  /** Summed qty of the filtered lines ("N cards match"); null from an older server. */
  matched_qty: number | null
  copies_rev: number
  summary: CopiesSummary
  facets?: CopiesFacets
}

/**
 * One pocket of a grouped search (copies group spec 3): every line of one
 * printing + finish (`printing`) or of one card (`name`), wants apart. The
 * printing fields are the REPRESENTATIVE line's.
 */
export interface CopyGroup {
  /** Opaque and stable across pages: the list key. Never parsed. */
  group_key: string
  kind: GroupKind
  card_name: string
  name_key: string
  scryfall_id: string | null
  set: string | null
  set_name: string | null
  collector_number: string | null
  rarity: string | null
  finish: CopyFinish
  /** Every finish in the group, sorted. */
  finishes: CopyFinish[]
  image_normal: string | null
  /** Every copy in the group (owned + trade + proxy for have; the wanted qty for want). */
  qty_total: number
  proxy_qty: number
  trade_qty: number
  line_count: number
  /** The line price when every line has the same one, else null. */
  unit_price_usd: number | null
  /** qty x line price over the value-carrying lines (proxies excluded); null when none is priced. */
  total_value_usd: number | null
  unpriced_qty: number
  added_at: string
  /**
   * The group's LINE ids (copy_id names a line, whose qty may exceed 1),
   * ascending, at most GROUP_COPY_IDS_MAX lines; line_count says how many
   * lines the group has.
   */
  copy_ids: number[]
  /** The group has more lines than copy_ids carries (the cap is lines, not copies). */
  copy_ids_more: boolean
  type_line?: string | null
  colors?: string[] | null
  color_identity?: string[] | null
  mv?: number | null
}

/** A grouped search page: `groups`, no `items` and no `total`. */
export interface CopiesGroupPage {
  group: Exclude<GroupMode, 'none'>
  groups: CopyGroup[]
  /** A grouped cursor (its own kind: never valid for a per-line search). */
  next_cursor: string | null
  total_groups: number
  /** Summed qty of the matching LINES (the per-line search's number). */
  matched_qty: number | null
  copies_rev: number
  summary: CopiesSummary
  facets?: CopiesFacets
}

/** The search filters as the UI holds them (every key optional). */
export interface CopiesFilters {
  q?: string | null
  status?: string[] | null
  set?: string[] | null
  finish?: string[] | null
  condition?: string[] | null
  language?: string[] | null
  color?: string[] | null
  color_mode?: ColorMode | null
  type?: string[] | null
  rarity?: string[] | null
  mv_min?: number | null
  mv_max?: number | null
  name_keys?: string[] | null
  /** Card-level search syntax (CardFilter's buildCopiesSearchRequest). */
  syntax?: string | null
  /**
   * The line-price bounds: DECIMAL STRINGS on the wire ("0.5", "10"), at most
   * 2 places. Numbers are accepted here and sent as their 2-place string.
   */
  price_min?: string | number | null
  price_max?: string | number | null
  /** ISO dates (YYYY-MM-DD), UTC days; added_to is inclusive. */
  added_from?: string | null
  added_to?: string | null
  qty_min?: number | null
  /** A manual or smart binder_id, or "unsorted" (binders spec 3.6). */
  binder?: BinderRef | null
  /** A groups `filter` key (collection views): exclusive color identity, W U B R G M C. */
  color_group?: string[] | null
  /** A groups `filter` key: the first matching type category. */
  type_group?: string[] | null
  /**
   * A binder / deck groups `filter` key: where the line physically sits
   * (Unsorted, a manual binder or a deck box). Added on top of `binder`
   * (kept, a smart binder included), so it narrows within the parent scope.
   */
  location?: BinderRef | null
}

/** The search key `binder`: a binder id or the virtual Unsorted binder. */
export type BinderRef = number | typeof UNSORTED
export const UNSORTED = 'unsorted' as const

/**
 * The canonical filter object the server hashes (spec 3.1b). Price bounds
 * (2-place decimal strings), added bounds (ISO days) and binder are ALWAYS
 * present (null when unset); syntax and qty_min join the object ONLY when
 * set, as on the server.
 */
export interface CanonicalFilters {
  q: string | null
  status: string[]
  set: string[]
  finish: string[]
  condition: string[]
  language: string[]
  color: string[]
  color_mode: ColorMode
  type: string[]
  rarity: string[]
  mv_min: string | null
  mv_max: string | null
  name_keys: string[]
  price_min: string | null
  price_max: string | null
  added_from: string | null
  added_to: string | null
  /**
   * The REQUEST's binder ref. The server hashes the RESOLVED binder identity
   * here instead ({binder_id, kind, rules_rev}); the client only needs a
   * stable query key, and every fixture without a binder has null on both.
   */
  binder: BinderRef | null
  syntax?: string
  qty_min?: number
  /** Joined ONLY when set (a groups `filter`), so every older canonical form is unchanged. */
  color_group?: string[]
  type_group?: string[]
  location?: BinderRef
  /** The search's top-level `group`, joined ONLY when it is not none (group spec 4). */
  group?: Exclude<GroupMode, 'none'>
}

export interface SearchRequest {
  filters?: CopiesFilters
  sort?: CopySort
  dir?: SortDir
  cursor?: string | null
  limit?: number
  /** Ask for facets over the filtered lines. */
  facets?: boolean
  /** Group the lines into pockets (`none`, the default, is never sent). */
  group?: GroupMode
  /** `include: ["spark"]`: the lines carry spark_7d (Showcase only). */
  include?: readonly CopiesInclude[]
}

export interface AddItem {
  scryfall_id?: string
  name?: string
  set?: string
  collector_number?: string
  finish?: CopyFinish
  qty?: number
  condition?: CopyCondition | string
  language?: string
  status?: CopyStatus
  purchase_price?: string
  purchase_currency?: string
  /** Add into this manual binder (merges only into a line in it). */
  binder_id?: number
}

export interface AddResponse {
  created: { index: number; copy_id: number }[]
  merged: { copy_id: number; qty: number }[]
  errors: { index: number; code: ItemErrorCode | string }[]
  copies_rev: number
  names_changed: boolean
}

export interface PatchBody {
  qty?: number
  status?: CopyStatus
  condition?: CopyCondition | null
  language?: string | null
  finish?: CopyFinish
  scryfall_id?: string
  set?: string
  collector_number?: string
  purchase_price?: string | null
  purchase_currency?: string | null
  split_qty?: number
  /** Move into a manual binder; null = Unsorted. With split_qty, moves that many. */
  binder_id?: number | null
  /**
   * Precondition: the line's qty must still be this, else 409 qty_mismatch
   * with current_qty (nothing changes). Sent on UI-derived moves and on
   * the deck editor's collection qty changes.
   */
  expected_qty?: number
  op_key?: string
}

/** Where a moved quantity landed (binders spec 2.5). */
export interface MoveResult {
  copy_id: number
  /** The line now holding the moved quantity (itself, a split, or a merge target). */
  result_copy_id: number
  /** The source line is gone (everything merged into result_copy_id). */
  source_deleted: boolean
}

export interface PatchResponse {
  copy_id: number
  copies_rev: number
  names_changed: boolean
  split_copy_id?: number
  deleted?: boolean
  /** Present when binder_id changed. */
  move?: MoveResult
}

export type BulkValue = string | number | null | { price: string; currency?: string }

export interface BulkResponse {
  updated: number
  deleted: number
  skipped: { copy_id: number; reason: SkipReason | string }[]
  copies_rev: number
  names_changed: boolean
  /** set_binder only: one entry per moved id, in request order. */
  results?: MoveResult[]
}

export interface ValuePoint {
  date: string
  value_usd: number
  cost_basis_usd: number | null
}

/** A daily sealed snapshot. Days before the first one are absent, not zero. */
export interface SealedPoint {
  day: string
  /** Decimal string, null when nothing owned was priced that day. */
  value_usd: string | null
  unpriced: number
}

export interface ValueHistory {
  points: ValuePoint[]
  current: {
    value_usd: number; cost_basis_usd: number | null; gain_usd: number | null; as_of: string | null
    /** Owned sealed value (decimal string), null when none is priced. */
    sealed_value_usd: string | null
  }
  change: { abs_usd: number; pct: number | null } | null
  first_date: string | null
  sealed_points: SealedPoint[]
  /** The first daily point on the current price basis; null until then. */
  basis_changed_on: string | null
}

export interface MissingPrinting {
  set: string
  collector_number: string
  qty_needed: number
  qty_have: number
}

/** A deck line's named printing, without quantities (`missing_cards[].printing`). */
export interface PrintingRef {
  set: string
  collector_number: string
}

export interface MissingCard {
  name: string
  qty_needed: number
  qty_have: number
  /** The first named printing, set and number only (quantities are in `printings`). */
  printing: PrintingRef | null
  printings: MissingPrinting[]
  have_other_printing: boolean
  /**
   * Copies on the card's WANT lines (front face match, every want line
   * summed; never part of have). null when the server did not send it (an
   * older API): the client then cannot tell what is already wanted.
   */
  qty_wanted: number | null
}

/** A printing the collection holds of a card (`owned_printings`). */
export interface OwnedPrinting {
  set: string
  collector_number: string
  finish: string
  proxy: boolean
  qty: number
}

/**
 * A printing the deck names that the collection holds none of, while it does
 * hold the card (covered or not).
 */
export interface OtherPrintingCard {
  name: string
  deck_printing: { set: string; collector_number: string }
  /** Real copies before proxies, most copies first, at most 3. */
  owned_printings: OwnedPrinting[]
  qty_needed: number
}

/**
 * A card whose have used proxies (real copies count first). The server sorts
 * these by qty_proxy x price, unknown prices last; sum(qty_proxy) == have_proxy.
 */
export interface ProxyCard {
  name: string
  qty_needed: number
  qty_have: number
  qty_proxy: number
  /** Cheapest known USD nonfoil paper price, or null. */
  unit_price_usd: number | null
}

export interface DeckOwnership {
  deck_id: string | null
  name: string | null
  commander: string | null
  card_count: number
  have: number
  have_proxy: number
  missing: number
  missing_cards: MissingCard[]
  /** Empty when the server did not send it (an older API). */
  other_printing_cards: OtherPrintingCard[]
  /** Empty when the server did not send it (an older API). */
  proxy_cards: ProxyCard[]
}

export interface DeckLinks {
  decks: DeckOwnership[]
  cards: Record<string, { deck_count: number; deck_ids: string[] }>
  decks_read: number
  decks_failed: boolean
  decks_truncated: boolean
  decks_skipped: number
}

export interface ExportResult {
  blob: Blob
  filename: string | null
}

// Bulk collection management (mtg-api plan 2026-10-30): select all matching
// (POST /copies/ids) and remove or move cards from a pasted list
// (POST /copies/match-list, /copies/take, /copies/take/undo).

/** One line a search matches: enough to bulk-write it and add it to a deck. */
export interface IdsLine {
  copy_id: number
  card_name: string
  qty: number
}

/** POST /copies/ids: every line the filters match, or `too_many` (no lines). */
export interface CopyIds {
  lines: IdsLine[]
  /** Lines matched (also when too_many). */
  total: number
  /** Copies matched (the sum of qty). */
  matched_qty: number
  copies_rev: number
  /** Over the server's cap (MAX_BULK_IDS): `lines` is empty. */
  too_many: boolean
}

export interface MatchAllocation {
  copy_id: number
  qty: number
}

/** A pasted line that named a card. */
export interface MatchCardRow {
  kind: typeof MATCH_ROW_KIND.card
  line_no: number
  text: string
  name: string
  qty_requested: number
  set: string | null
  collector_number: string | null
  finish: string | null
  status: MatchStatus
  /** Owned lines this row may take from, best first (server order). */
  candidate_ids: number[]
  /** The server's first allocation (one shared per-line budget, input order). */
  allocation: MatchAllocation[]
}

/** A line the parser skipped on purpose (comment, section, tokens). */
export interface MatchIgnoredRow {
  kind: typeof MATCH_ROW_KIND.ignored
  line_no: number
  text: string
  reason: string
}

/** A line the parser could not read (bad_qty, empty_name, unreadable). */
export interface MatchErrorRow {
  kind: typeof MATCH_ROW_KIND.error
  line_no: number
  text: string
  error_code: string
}

export type MatchRow = MatchCardRow | MatchIgnoredRow | MatchErrorRow

/** A candidate line as the review shows it. */
export interface MatchLine {
  card_name: string
  set: string | null
  collector_number: string | null
  finish: string | null
  condition: string | null
  language: string | null
  status: string
  binder_id: number | null
  /** The line's qty when matched: the per-line budget and the take's expected_qty. */
  qty: number
  price: number | null
}

export interface MatchList {
  rows: MatchRow[]
  /** Every candidate line once, by copy_id. */
  lines: ReadonlyMap<number, MatchLine>
  distinct_lines_allocated: number
  /** The first allocation spans more than MAX_TAKE_LINES lines. */
  over_limit: boolean
  copies_rev: number
}

/** One aggregated take item: each copy_id once (a duplicate is a 422). */
export interface TakeItem {
  copy_id: number
  qty: number
  /** The line's qty as matched: a line changed since is skipped qty_mismatch. */
  expected_qty: number
}

export interface TakeSkip {
  copy_id: number
  reason: SkipReason | string
  /** qty_mismatch: what the line holds now. */
  current_qty?: number
}

export interface TakeResponse {
  action: TakeAction
  /** removed_qty or moved_qty, whichever the action answers. */
  qty: number
  updated: number
  deleted: number
  created: number
  skipped: TakeSkip[]
  /** Per item: what was applied (0 with a reason for a same-binder move). */
  applied: { copy_id: number; qty: number; reason?: string }[]
  copies_rev: number
  names_changed: boolean
  /** A remove that applied anything: undo it with POST /copies/take/undo. */
  undo_token?: string
}

export interface TakeUndoResponse {
  restored_qty: number
  /** A line that was deleted comes back under a NEW copy_id (result_copy_id). */
  restored: { copy_id: number; qty: number; result_copy_id: number }[]
  copies_rev: number
  names_changed: boolean
}

// --- Errors -------------------------------------------------------------------

/**
 * Every failure of a per-copy call, as ONE thrown type. `kind` is the union a
 * caller branches on; `detail` is the server's machine code or 422 sentence
 * (never shown raw: screens map kinds and codes to catalog copy).
 */
export class CopiesError extends Error {
  readonly kind: CopiesErrorKind
  readonly status?: number
  readonly detail?: string
  /**
   * The flattened `{code, message}` code of the search errors
   * (invalid_syntax, query_too_broad), read separately from `detail` so it
   * never feeds the capability classification (Codex r1 #9).
   */
  readonly code?: string
  /**
   * The error body's other machine fields, when it was an object: `field`
   * (duplicate_condition, empty_range), `binders_rev` (binders_rev_mismatch),
   * `current_qty` (qty_mismatch, insufficient_qty).
   */
  readonly body?: Readonly<Record<string, unknown>>
  /** The response's raw Retry-After header (429 / 503), when it had one. */
  readonly retryAfter?: string | null

  constructor(kind: CopiesErrorKind, opts: { status?: number; detail?: string; code?: string; body?: Record<string, unknown>; retryAfter?: string | null } = {}) {
    // eslint-disable-next-line i18next/no-literal-string -- developer message, never rendered
    super(`copies ${kind}${opts.status ? ` (${opts.status})` : ''}${opts.detail ? `: ${opts.detail}` : ''}`)
    // eslint-disable-next-line i18next/no-literal-string -- class name
    this.name = 'CopiesError'
    this.kind = kind
    this.status = opts.status
    this.detail = opts.detail
    this.code = opts.code
    this.body = opts.body
    this.retryAfter = opts.retryAfter
  }

  /** An ambiguous failure the SAME request (same op_key) may be re-sent after. */
  get retryable(): boolean {
    return this.kind === ERR.network || this.kind === ERR.server
      || this.kind === ERR.rateLimited || this.kind === ERR.serviceUnavailable
  }
}

export function isCopiesError(e: unknown): e is CopiesError {
  return e instanceof CopiesError
}

/** C1: the capability a failure moves the pair to, or null for none. */
export function capabilityForError(kind: CopiesErrorKind): Exclude<Capability, 'per_copy' | 'unknown'> | null {
  switch (kind) {
    case ERR.notFound:
    case ERR.forbidden:
    case ERR.claimRequired:
      return CAPABILITY.nameLevel
    case ERR.copiesUnavailable:
      return CAPABILITY.rebuilding
    case ERR.unauthorized:
      return CAPABILITY.unavailable
    default:
      return null
  }
}

/** `detail` as a machine code: a string, or an object's `code` / `key`. */
export function parseDetail(body: unknown): string | undefined {
  if (!body || typeof body !== 'object') return undefined
  const d = (body as { detail?: unknown }).detail
  if (typeof d === 'string') return d.trim() || undefined
  if (d && typeof d === 'object') {
    const o = d as { code?: unknown; key?: unknown }
    if (typeof o.code === 'string' && o.code.trim()) return o.code.trim()
    if (typeof o.key === 'string' && o.key.trim()) return o.key.trim()
  }
  return undefined
}

/** The search error codes read from a 422 body (never used to classify). */
export const SEARCH_ERROR_CODES = new Set(['invalid_syntax', 'query_too_broad'])

/**
 * The code of a search 422: an object detail {code, message} reaches the
 * wire flattened (`code` at the top level, `detail` the message). Only the
 * search codes count; anything else is undefined.
 */
export function parseSearchCode(body: unknown): string | undefined {
  if (!body || typeof body !== 'object') return undefined
  const o = body as { code?: unknown; detail?: unknown }
  const raw = typeof o.code === 'string' ? o.code
    : o.detail && typeof o.detail === 'object' ? (o.detail as { code?: unknown }).code : undefined
  return typeof raw === 'string' && SEARCH_ERROR_CODES.has(raw.trim()) ? raw.trim() : undefined
}

/**
 * The body's top-level `error` string (the API's error envelope names the
 * class there, and the spare rule's 503 may name decks_unavailable there),
 * else a `detail` object's `error`. Read ONLY to tell decks_unavailable from
 * other 503s, never to classify anything else.
 */
export function parseErrorField(body: unknown): string | undefined {
  if (!body || typeof body !== 'object' || Array.isArray(body)) return undefined
  const o = body as { error?: unknown; detail?: unknown }
  if (typeof o.error === 'string' && o.error.trim()) return o.error.trim()
  if (o.detail && typeof o.detail === 'object') {
    const e = (o.detail as { error?: unknown }).error
    if (typeof e === 'string' && e.trim()) return e.trim()
  }
  return undefined
}

/**
 * The C1 matrix: an HTTP status plus its detail code to an error kind.
 * `errorField` (parseErrorField) only matters for a 503: decks_unavailable
 * may arrive there instead of in `detail`.
 */
export function classifyFailure(status: number, detail: string | undefined, errorField?: string): CopiesErrorKind {
  if (status === 404) {
    // R2-1: ONLY the collection-level `not_found` downgrades. A copy that is
    // gone is an item error; any other 404 (a framework "Not Found", a proxy's
    // HTML page, an unreadable body) proves nothing about the collection and
    // is a non-downgrading server error.
    if (detail === DETAIL.copyNotFound) return ERR.copyNotFound
    // Binder and sealed rows that are gone are ITEM errors too.
    if (detail === DETAIL.binderNotFound) return ERR.binderNotFound
    if (detail === DETAIL.sealedNotFound) return ERR.sealedNotFound
    if (detail === DETAIL.notFound) return ERR.notFound
    return ERR.server
  }
  if (status === 401) return ERR.unauthorized
  if (status === 403) return ERR.forbidden
  if (status === 409) {
    if (detail === DETAIL.claimRequired) return ERR.claimRequired
    if (detail === DETAIL.copiesUnavailable) return ERR.copiesUnavailable
    if (detail === DETAIL.opKeyInUse) return ERR.opKeyInUse
    return ERR.conflict
  }
  if (status === 422 || status === 400) return ERR.validation
  // Gone for good (an expired or voided take undo token): definitive, never
  // retried as a server error would be.
  if (status === 410) return ERR.gone
  if (status === 429) return ERR.rateLimited
  // Not an outage: the server has not migrated yet. Never retried; the
  // binders UI hides.
  if (status === 503 && detail === DETAIL.bindersNotReady) return ERR.bindersNotReady
  // My Decks could not be read (the spare rule): its own kind, never backed
  // off as an outage (the server already retried its deck read).
  if (status === 503 && (detail === DETAIL.decksUnavailable || errorField === DETAIL.decksUnavailable)) return ERR.decksUnavailable
  // A deck grouping before the deck boxes are readable: like decks_unavailable, not an outage.
  if (status === 503 && detail === DETAIL.decksNotReady) return ERR.decksUnavailable
  if (status === 503) return ERR.serviceUnavailable
  return ERR.server
}

// --- Transport ----------------------------------------------------------------

export interface CopiesContext {
  userId: string
  collectionId: string
  signal?: AbortSignal
  /**
   * Exactly ONE request: no 401 refresh-and-resend, no 429/503 backoff. The
   * mutation reconcile uses it so each of its attempts is bounded by its own
   * per-request timeout and the whole reconcile by one deadline.
   */
  singleAttempt?: boolean
}

interface RequestSpec {
  method: string
  /** Path AFTER /collections/{id}, e.g. `/copies/search`. */
  sub: string
  query?: Record<string, string | number | undefined>
  body?: unknown
  /** The capability probe: only its success raises the pair to per_copy. */
  probe?: boolean
  /**
   * Binder and sealed calls: their outcome never moves the capability (the
   * copies routes own it). A binders 404 must not take the per-copy screen
   * down with it.
   */
  capabilityNeutral?: boolean
  /**
   * No automatic 429 / 503 backoff: the caller paces itself (the binder
   * preview's rolling limiter) and reads Retry-After from the error.
   */
  noBackoff?: boolean
  /** Extra request headers (the binder layout's If-None-Match). */
  headers?: Record<string, string>
  /**
   * Non-2xx statuses that are an ANSWER, not a failure, and come back as the
   * Response (the binder layout's 304 Not Modified).
   */
  okStatuses?: readonly number[]
}

const MAX_BACKOFF_RETRIES = 3
const BASE_BACKOFF_MS = 400
const MAX_BACKOFF_MS = 30_000

type Sleep = (ms: number, signal?: AbortSignal) => Promise<void>

const realSleep: Sleep = (ms, signal) => new Promise<void>((resolve, reject) => {
  if (signal?.aborted) { reject(new CopiesError(ERR.aborted)); return }
  const t = setTimeout(() => { signal?.removeEventListener('abort', onAbort); resolve() }, ms)
  const onAbort = () => { clearTimeout(t); reject(new CopiesError(ERR.aborted)) }
  // eslint-disable-next-line i18next/no-literal-string -- DOM event name
  signal?.addEventListener('abort', onAbort, { once: true })
})

let sleep: Sleep = realSleep
let random: () => number = Math.random

/** Test seam: replace the backoff sleep and jitter source. */
export function __setCopiesTiming(s: Sleep | null, r: (() => number) | null = null): void {
  sleep = s ?? realSleep
  random = r ?? Math.random
}

/** Retry-After as milliseconds (delta-seconds or an HTTP date), capped. */
export function retryAfterMs(header: string | null, now = Date.now()): number | null {
  if (!header) return null
  const trimmed = header.trim()
  if (/^\d+$/.test(trimmed)) return Math.min(parseInt(trimmed, 10) * 1000, MAX_BACKOFF_MS)
  const at = Date.parse(trimmed)
  if (Number.isNaN(at)) return null
  return Math.min(Math.max(0, at - now), MAX_BACKOFF_MS)
}

/** Jittered exponential backoff for retry `attempt` (0-based). */
export function backoffMs(attempt: number, rnd: () => number = random): number {
  const base = Math.min(BASE_BACKOFF_MS * 2 ** attempt, MAX_BACKOFF_MS)
  return Math.round(base * (0.5 + rnd() * 0.5))
}

function buildUrl(ctx: CopiesContext, spec: RequestSpec): string {
  const qs = new URLSearchParams()
  for (const [k, v] of Object.entries(spec.query ?? {})) {
    if (v !== undefined && v !== '') qs.set(k, String(v))
  }
  const q = qs.toString()
  return apiUrl(`${SEG.collections}${encodeURIComponent(ctx.collectionId)}${spec.sub}${q ? `?${q}` : ''}`)
}

async function readJson(res: Response): Promise<unknown> {
  try {
    return await res.json()
  } catch {
    return undefined
  }
}

function report(ctx: CopiesContext, spec: RequestSpec, outcome: CopiesError | null, generationAtStart: number): void {
  if (spec.capabilityNeutral) return
  if (!outcome) {
    if (spec.probe) markPerCopyIfUnchanged(ctx.userId, ctx.collectionId, generationAtStart)
    else confirmPerCopy(ctx.userId, ctx.collectionId)
    return
  }
  // A PROBE that fails at the network level is the server being dark: with
  // COLLECTION_COPIES off the API answers the CORS preflight with a bare 404
  // (no CORS headers), which a browser surfaces as a TypeError, never as a
  // readable 404 (API 1cd817b0, spec section 7). So for the probe only, a
  // network error means name_level, silently. A network error on any other
  // call stays a transient failure (retry, backoff) and moves nothing.
  if (spec.probe && outcome.kind === ERR.network) {
    downgrade(ctx.userId, ctx.collectionId, CAPABILITY.nameLevel)
    return
  }
  const next = capabilityForError(outcome.kind)
  if (next) downgrade(ctx.userId, ctx.collectionId, next, { forbidden: outcome.kind === ERR.forbidden })
}

function abortedError(): CopiesError {
  return new CopiesError(ERR.aborted)
}

/**
 * One per-copy request with the full transport policy. Resolves to the OK
 * Response (the caller decodes it); throws CopiesError otherwise. Every
 * terminal outcome is reported to the capability store.
 */
export type CopiesRequestSpec = RequestSpec

export async function copiesFetch(ctx: CopiesContext, spec: RequestSpec): Promise<Response> {
  // Captured BEFORE anything is sent: a probe may only promote the pair if no
  // downgrade (or other change) happened while it was in the air.
  const generationAtStart = getCapability(ctx.userId, ctx.collectionId).generation
  try {
    const res = await send(ctx, spec)
    report(ctx, spec, null, generationAtStart)
    // A write landed: the offline copy of this collection is refreshed (W3).
    if (spec.method !== HTTP.get) noteCollectionWrite(ctx.collectionId)
    return res
  } catch (e) {
    const err = e instanceof CopiesError ? e : new CopiesError(ERR.network)
    report(ctx, spec, err, generationAtStart)
    throw err
  }
}

/**
 * Before EVERY resend (after a 401 refresh or a 429/503 backoff): the live
 * user must still be the one the request is for (the original token is kept
 * and nothing is ever minted for someone else), and, for anything but the
 * probe, the pair must not have been downgraded meanwhile.
 */
function assertMayResend(ctx: CopiesContext, spec: RequestSpec): void {
  if (ctx.signal?.aborted) throw abortedError()
  // The collection OWNER (an anonymous session is a guest, never an owner).
  if (collectionOwnerOf(getSupabaseIdentity()) !== ctx.userId) throw new CopiesError(ERR.authRefused)
  if (!spec.probe && isDowngraded(getCapability(ctx.userId, ctx.collectionId).state)) throw abortedError()
}

async function send(ctx: CopiesContext, spec: RequestSpec): Promise<Response> {
  const url = buildUrl(ctx, spec)
  const { signal } = ctx
  if (signal?.aborted) throw abortedError()

  let auth = await writeAuthFor(url, ctx.userId)
  if (!auth.ok || auth.userId !== ctx.userId || !auth.token) throw new CopiesError(ERR.authRefused)
  if (signal?.aborted) throw abortedError()

  const init = (headers: Record<string, string>): RequestInit => {
    // Every per-copy and binder request declares what this client reads
    // (CLIENT_CAPS): without it a server answers the old shapes only.
    const h: Record<string, string> = { ...spec.headers, ...headers, [HTTP.caps]: CLIENT_CAPS.join(',') }
    if (spec.body !== undefined) h[HTTP.contentType] = HTTP.json
    return {
      method: spec.method,
      headers: h,
      body: spec.body !== undefined ? JSON.stringify(spec.body) : undefined,
      // eslint-disable-next-line i18next/no-literal-string -- fetch option: purchase prices must never be served from the HTTP cache
      cache: 'no-store',
      signal,
    }
  }

  let refreshed = false
  let backoffs = 0
  for (;;) {
    let res: Response
    try {
      res = await apiFetch(url, init(auth.headers))
    } catch {
      if (signal?.aborted) throw abortedError()
      throw new CopiesError(ERR.network)
    }
    if (res.ok || spec.okStatuses?.includes(res.status)) return res

    const body = await readJson(res)
    const detail = parseDetail(body)
    const kind = classifyFailure(res.status, detail, parseErrorField(body))
    const code = res.status === 422 ? parseSearchCode(body) : undefined
    const extra = body && typeof body === 'object' && !Array.isArray(body) ? body as Record<string, unknown> : undefined
    const retryAfter = res.headers.get(HTTP.retryAfter)

    if (ctx.singleAttempt) throw new CopiesError(kind, { status: res.status, detail, code, body: extra, retryAfter })

    if (kind === ERR.unauthorized && !refreshed) {
      // Refresh ONCE, for the same user only, then resend with the new token.
      refreshed = true
      const live = await refreshSessionIdentity()
      if (signal?.aborted) throw abortedError()
      if (!live.ok || live.userId !== ctx.userId) throw new CopiesError(ERR.unauthorized, { status: 401, detail })
      auth = await writeAuthFor(url, ctx.userId)
      if (!auth.ok || auth.userId !== ctx.userId || !auth.token) throw new CopiesError(ERR.unauthorized, { status: 401, detail })
      assertMayResend(ctx, spec)
      continue
    }

    if ((kind === ERR.rateLimited || kind === ERR.serviceUnavailable) && backoffs < MAX_BACKOFF_RETRIES && !spec.noBackoff) {
      // Same captured token: a 503 is an auth OUTAGE, never a reason to sign out.
      const wait = retryAfterMs(res.headers.get(HTTP.retryAfter)) ?? backoffMs(backoffs)
      backoffs += 1
      await sleep(wait, signal)
      assertMayResend(ctx, spec)
      continue
    }

    throw new CopiesError(kind, { status: res.status, detail, code, body: extra, retryAfter })
  }
}

// --- Decoders -----------------------------------------------------------------
//
// Strict where the client depends on a field, tolerant of additions. A shape
// the client cannot use throws `decode` (never a half-typed object).

type Obj = Record<string, unknown>

function fail(): never {
  throw new CopiesError(ERR.decode)
}
function obj(v: unknown): Obj {
  if (!v || typeof v !== 'object' || Array.isArray(v)) fail()
  return v as Obj
}
function num(v: unknown): number {
  if (typeof v !== 'number' || !Number.isFinite(v)) fail()
  return v
}
function numOrNull(v: unknown): number | null {
  return v === null || v === undefined ? null : num(v)
}
function str(v: unknown): string {
  if (typeof v !== 'string') fail()
  return v
}
function strOrNull(v: unknown): string | null {
  return v === null || v === undefined ? null : str(v)
}
function bool(v: unknown): boolean {
  if (typeof v !== 'boolean') fail()
  return v
}
function arr(v: unknown): unknown[] {
  if (!Array.isArray(v)) fail()
  return v
}

export function decodeCopyItem(raw: unknown): CopyItem {
  const o = obj(raw)
  return {
    copy_id: num(o.copy_id),
    card_name: str(o.card_name),
    name_key: str(o.name_key),
    scryfall_id: strOrNull(o.scryfall_id),
    set: strOrNull(o.set),
    set_name: strOrNull(o.set_name),
    collector_number: strOrNull(o.collector_number),
    rarity: strOrNull(o.rarity),
    finish: str(o.finish) as CopyFinish,
    condition: strOrNull(o.condition) as CopyCondition | null,
    language: strOrNull(o.language),
    status: str(o.status) as CopyStatus,
    qty: num(o.qty),
    // A decimal STRING on the wire. A server that ever sent a number is read
    // back into a string without float arithmetic.
    purchase_price: typeof o.purchase_price === 'number' ? String(o.purchase_price) : strOrNull(o.purchase_price),
    purchase_currency: strOrNull(o.purchase_currency),
    source: typeof o.source === 'string' ? o.source : '',
    added_at: typeof o.added_at === 'string' ? o.added_at : '',
    unit_price_usd: numOrNull(o.unit_price_usd),
    image_normal: strOrNull(o.image_normal),
    // Absent from a server that predates binders: Unsorted.
    binder_id: numOrNull(o.binder_id),
    ...cardMeta(o),
    ...changeMeta(o),
  }
}

/**
 * The additive 7D change fields, decoded TOLERANTLY: a bad value is unknown
 * (dropped), never a decode failure of the line. Exported for the snapshot.
 */
export function changeMeta(o: Obj): Pick<CopyItem, 'price_change_7d_pct' | 'price_change_base_day' | 'spark_7d'> {
  const out: Pick<CopyItem, 'price_change_7d_pct' | 'price_change_base_day' | 'spark_7d'> = {}
  if ('price_change_7d_pct' in o) {
    out.price_change_7d_pct = typeof o.price_change_7d_pct === 'number' && Number.isFinite(o.price_change_7d_pct) ? o.price_change_7d_pct : null
  }
  if ('price_change_base_day' in o) out.price_change_base_day = typeof o.price_change_base_day === 'string' ? o.price_change_base_day : null
  if (Array.isArray(o.spark_7d)) {
    const pts: SparkPoint[] = []
    for (const raw of o.spark_7d) {
      if (!raw || typeof raw !== 'object') continue
      const p = raw as Obj
      if (typeof p.d !== 'string') continue
      pts.push({ d: p.d, usd: typeof p.usd === 'number' && Number.isFinite(p.usd) ? p.usd : null })
    }
    if (pts.filter(p => p.usd !== null).length >= 2) out.spark_7d = pts
  }
  return out
}

/** The additive card fields: kept only when present, tolerant of bad values. */
function cardMeta(o: Obj): Pick<CopyItem, 'type_line' | 'colors' | 'color_identity' | 'mv'> {
  const out: Pick<CopyItem, 'type_line' | 'colors' | 'color_identity' | 'mv'> = {}
  if ('type_line' in o) out.type_line = typeof o.type_line === 'string' ? o.type_line : null
  if ('colors' in o) out.colors = Array.isArray(o.colors) ? o.colors.filter((c): c is string => typeof c === 'string') : null
  if ('color_identity' in o) out.color_identity = Array.isArray(o.color_identity) ? o.color_identity.filter((c): c is string => typeof c === 'string') : null
  if ('mv' in o) out.mv = typeof o.mv === 'number' && Number.isFinite(o.mv) ? o.mv : null
  return out
}

function facetCounts(v: unknown): FacetCount[] {
  if (!Array.isArray(v)) return []
  const out: FacetCount[] = []
  for (const raw of v) {
    if (!raw || typeof raw !== 'object') continue
    const x = raw as Obj
    if (typeof x.value === 'string' && typeof x.count === 'number') out.push({ value: x.value, count: x.count })
  }
  return out
}

export function decodeFacets(raw: unknown): CopiesFacets | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined
  const o = raw as Obj
  const sets: SetFacet[] = []
  if (Array.isArray(o.sets)) {
    for (const s of o.sets) {
      if (!s || typeof s !== 'object') continue
      const x = s as Obj
      if (typeof x.code === 'string' && typeof x.count === 'number') {
        sets.push({ code: x.code, name: typeof x.name === 'string' ? x.name : null, count: x.count })
      }
    }
  }
  return {
    sets,
    rarities: facetCounts(o.rarities),
    finishes: facetCounts(o.finishes),
    types: facetCounts(o.types),
    subtypes_top: facetCounts(o.subtypes_top),
  }
}

export function decodeSummary(raw: unknown): CopiesSummary {
  const o = obj(raw)
  const s: CopiesSummary = {
    owned: num(o.owned),
    trade: num(o.trade),
    proxy: num(o.proxy),
    want: num(o.want),
    size_cards: num(o.size_cards),
    size_unique: num(o.size_unique),
    last_changed_at: strOrNull(o.last_changed_at),
  }
  // ABSENT on later pages (not null): keep absent as absent.
  if ('want_value_usd' in o) s.want_value_usd = numOrNull(o.want_value_usd)
  if ('want_unpriced' in o && o.want_unpriced !== null) s.want_unpriced = num(o.want_unpriced)
  return s
}

// --- The response revision of a line ------------------------------------------
//
// Every CopyItem a page or a groups page decodes is stamped with the
// copies_rev of THAT response, on the object itself (a WeakMap, so it never
// shows in the line's fields or in an equality check). Whoever holds the
// object (a selection, a menu target, a stale preview) holds its rev; no
// separately updated map can pair an old line with a newer rev.

const LINE_REVS = new WeakMap<CopyItem, number>()

/** The copies_rev of the response that produced this line object; undefined when not known. */
export function lineRevOf(item: CopyItem): number | undefined {
  return LINE_REVS.get(item)
}

/**
 * Stamps a line with its response's copies_rev, never lowering it: a later
 * response with an EQUAL line (TanStack keeps the old object then, structural
 * sharing) proves the same content at the later rev.
 */
export function stampLineRev(item: CopyItem, rev: number | null | undefined): void {
  if (typeof rev !== 'number' || !Number.isFinite(rev)) return
  const had = LINE_REVS.get(item)
  if (had === undefined || rev > had) LINE_REVS.set(item, rev)
}

/**
 * TanStack's structural sharing keeps the OLD object when a new response
 * holds an equal line. That old object may be unstamped (an optimistic
 * edit's, or a rollback's) or stamped earlier; the response just proved its
 * content at the response's rev, so it takes the NEW object's stamp. Only a
 * stamped new object passes one on: an optimistic setQueryData brings new
 * unstamped objects (and the page's pre-edit rev never touches them), so an
 * optimistic line stays unknown until a real response replaces it.
 *
 * `shared` is replaceEqualDeep(old, next); every line object of it that is
 * not the next object for the same position of `next` was kept as equal.
 */
export function carryLineRevs(shared: unknown, next: unknown): void {
  const linesOf = (data: unknown): CopyItem[] => {
    const pages = (data as { pages?: unknown[] } | undefined)?.pages
    if (!Array.isArray(pages)) return []
    const out: CopyItem[] = []
    for (const p of pages as { items?: CopyItem[]; groups?: { preview?: CopyItem[] }[] }[]) {
      if (Array.isArray(p?.items)) out.push(...p.items)
      if (Array.isArray(p?.groups)) for (const g of p.groups) if (Array.isArray(g?.preview)) out.push(...g.preview)
    }
    return out
  }
  const a = linesOf(shared)
  const b = linesOf(next)
  if (a.length !== b.length) return
  for (let i = 0; i < a.length; i++) {
    if (a[i] === b[i]) continue
    const rev = LINE_REVS.get(b[i])
    if (rev !== undefined) stampLineRev(a[i], rev)
  }
}

export function decodeCopiesPage(raw: unknown): CopiesPage {
  const o = obj(raw)
  const page: CopiesPage = {
    items: arr(o.items).map(decodeCopyItem),
    next_cursor: strOrNull(o.next_cursor),
    total: num(o.total),
    matched_qty: typeof o.matched_qty === 'number' && Number.isFinite(o.matched_qty) ? o.matched_qty : null,
    copies_rev: num(o.copies_rev),
    summary: decodeSummary(o.summary),
  }
  const facets = decodeFacets(o.facets)
  if (facets) page.facets = facets
  for (const it of page.items) stampLineRev(it, page.copies_rev)
  return page
}

function intList(v: unknown): number[] {
  return arr(v).map(num)
}

export function decodeCopyGroup(raw: unknown): CopyGroup {
  const o = obj(raw)
  const kind = str(o.kind)
  if (kind !== GROUP_KIND.have && kind !== GROUP_KIND.want) fail()
  return {
    group_key: str(o.group_key),
    kind,
    card_name: str(o.card_name),
    name_key: str(o.name_key),
    scryfall_id: strOrNull(o.scryfall_id),
    set: strOrNull(o.set),
    set_name: strOrNull(o.set_name),
    collector_number: strOrNull(o.collector_number),
    rarity: strOrNull(o.rarity),
    finish: str(o.finish) as CopyFinish,
    finishes: Array.isArray(o.finishes) ? o.finishes.filter((f): f is CopyFinish => typeof f === 'string') : [],
    image_normal: strOrNull(o.image_normal),
    qty_total: num(o.qty_total),
    proxy_qty: typeof o.proxy_qty === 'number' ? o.proxy_qty : 0,
    trade_qty: typeof o.trade_qty === 'number' ? o.trade_qty : 0,
    line_count: num(o.line_count),
    unit_price_usd: numOrNull(o.unit_price_usd),
    total_value_usd: numOrNull(o.total_value_usd),
    unpriced_qty: typeof o.unpriced_qty === 'number' ? o.unpriced_qty : 0,
    added_at: typeof o.added_at === 'string' ? o.added_at : '',
    copy_ids: intList(o.copy_ids),
    copy_ids_more: bool(o.copy_ids_more),
    ...cardMeta(o),
  }
}

/**
 * A grouped search page. A 200 WITHOUT `groups` is a server that ignored the
 * key (it answered per line): a `no groups` decode error, which the caller
 * reads as "grouping unsupported" (isGroupUnsupported).
 */
export function decodeCopiesGroupPage(raw: unknown): CopiesGroupPage {
  const o = obj(raw)
  if (!Array.isArray(o.groups)) throw new CopiesError(ERR.decode, { detail: DETAIL.noGroups })
  const group = str(o.group)
  if (group !== GROUP_MODE.printing && group !== GROUP_MODE.name) fail()
  const page: CopiesGroupPage = {
    group,
    groups: o.groups.map(decodeCopyGroup),
    next_cursor: strOrNull(o.next_cursor),
    total_groups: num(o.total_groups),
    matched_qty: typeof o.matched_qty === 'number' && Number.isFinite(o.matched_qty) ? o.matched_qty : null,
    copies_rev: num(o.copies_rev),
    summary: decodeSummary(o.summary),
  }
  const facets = decodeFacets(o.facets)
  if (facets) page.facets = facets
  return page
}

/**
 * The server cannot group: a 422 `bad group`, an older server's 422
 * `unknown field(s): ..., group, ...` (it predates the key), or a 200 with no
 * `groups`. The client then falls back to Each copy for the session. Any
 * other error (a grouped sort 422, a bad cursor, an outage) is NOT this.
 */
export function isGroupUnsupported(e: unknown): boolean {
  if (!isCopiesError(e)) return false
  if (e.kind === ERR.decode) return e.detail === DETAIL.noGroups
  if (e.kind !== ERR.validation || !e.detail) return false
  if (e.detail === DETAIL.badGroup) return true
  if (!e.detail.startsWith(DETAIL.unknownFieldPrefix)) return false
  return e.detail.slice(DETAIL.unknownFieldPrefix.length).split(',').some(f => f.trim() === DETAIL.groupField)
}

export function decodeAddResponse(raw: unknown): AddResponse {
  const o = obj(raw)
  return {
    created: arr(o.created).map(c => { const x = obj(c); return { index: num(x.index), copy_id: num(x.copy_id) } }),
    merged: arr(o.merged).map(m => { const x = obj(m); return { copy_id: num(x.copy_id), qty: num(x.qty) } }),
    errors: arr(o.errors).map(e => { const x = obj(e); return { index: num(x.index), code: str(x.code) } }),
    copies_rev: num(o.copies_rev),
    names_changed: o.names_changed === undefined ? true : bool(o.names_changed),
  }
}

export function decodePatchResponse(raw: unknown): PatchResponse {
  const o = obj(raw)
  const r: PatchResponse = {
    copy_id: num(o.copy_id),
    copies_rev: num(o.copies_rev),
    names_changed: o.names_changed === undefined ? true : bool(o.names_changed),
  }
  if (o.split_copy_id !== undefined && o.split_copy_id !== null) r.split_copy_id = num(o.split_copy_id)
  if (o.deleted === true) r.deleted = true
  if (o.move !== undefined && o.move !== null) r.move = decodeMoveResult(o.move)
  return r
}

export function decodeMoveResult(raw: unknown): MoveResult {
  const x = obj(raw)
  return { copy_id: num(x.copy_id), result_copy_id: num(x.result_copy_id), source_deleted: bool(x.source_deleted) }
}

export function decodeBulkResponse(raw: unknown): BulkResponse {
  const o = obj(raw)
  return {
    updated: num(o.updated),
    deleted: num(o.deleted),
    skipped: arr(o.skipped).map(s => { const x = obj(s); return { copy_id: num(x.copy_id), reason: str(x.reason) } }),
    copies_rev: num(o.copies_rev),
    names_changed: o.names_changed === undefined ? true : bool(o.names_changed),
    ...(Array.isArray(o.results) ? { results: o.results.map(decodeMoveResult) } : {}),
  }
}

export function decodeCopyIds(raw: unknown): CopyIds {
  const o = obj(raw)
  return {
    lines: arr(o.lines).map(l => { const x = obj(l); return { copy_id: num(x.copy_id), card_name: str(x.card_name), qty: num(x.qty) } }),
    total: num(o.total),
    matched_qty: num(o.matched_qty),
    copies_rev: num(o.copies_rev),
    too_many: bool(o.too_many),
  }
}

const MATCH_STATUS_SET = new Set<string>(MATCH_STATUSES)

function decodeMatchRow(raw: unknown): MatchRow {
  const x = obj(raw)
  const base = { line_no: num(x.line_no), text: str(x.text) }
  if (x.kind === MATCH_ROW_KIND.card) {
    const status = str(x.status)
    if (!MATCH_STATUS_SET.has(status)) fail()
    return {
      ...base,
      kind: MATCH_ROW_KIND.card,
      name: str(x.name),
      qty_requested: num(x.qty_requested),
      set: strOrNull(x.set),
      collector_number: strOrNull(x.collector_number),
      finish: strOrNull(x.finish),
      status: status as MatchStatus,
      candidate_ids: arr(x.candidate_ids).map(num),
      allocation: arr(x.allocation).map(a => { const y = obj(a); return { copy_id: num(y.copy_id), qty: num(y.qty) } }),
    }
  }
  if (x.kind === MATCH_ROW_KIND.error) return { ...base, kind: MATCH_ROW_KIND.error, error_code: str(x.error_code) }
  // An ignored row, or a kind this client does not know: listed as skipped,
  // never acted on.
  return { ...base, kind: MATCH_ROW_KIND.ignored, reason: typeof x.reason === 'string' ? x.reason : str(x.kind) }
}

function decodeMatchLine(raw: unknown): MatchLine {
  const x = obj(raw)
  return {
    card_name: str(x.card_name),
    set: strOrNull(x.set),
    collector_number: strOrNull(x.collector_number),
    finish: strOrNull(x.finish),
    condition: strOrNull(x.condition),
    language: strOrNull(x.language),
    status: str(x.status),
    binder_id: numOrNull(x.binder_id),
    qty: num(x.qty),
    price: numOrNull(x.price),
  }
}

export function decodeMatchList(raw: unknown): MatchList {
  const o = obj(raw)
  const lines = new Map<number, MatchLine>()
  for (const [k, v] of Object.entries(obj(o.lines))) {
    const id = Number(k)
    if (!Number.isInteger(id)) fail()
    lines.set(id, decodeMatchLine(v))
  }
  const rows = arr(o.rows).map(decodeMatchRow)
  // Every candidate must be described: its budget and expected_qty come from `lines`.
  for (const r of rows) if (r.kind === MATCH_ROW_KIND.card && r.candidate_ids.some(id => !lines.has(id))) fail()
  return {
    rows,
    lines,
    distinct_lines_allocated: num(o.distinct_lines_allocated),
    over_limit: bool(o.over_limit),
    copies_rev: num(o.copies_rev),
  }
}

export function decodeTakeResponse(raw: unknown, action: TakeAction): TakeResponse {
  const o = obj(raw)
  const r: TakeResponse = {
    action,
    qty: num(action === 'move' ? o.moved_qty : o.removed_qty),
    updated: num(o.updated),
    deleted: num(o.deleted),
    created: num(o.created),
    skipped: arr(o.skipped).map(s => {
      const x = obj(s)
      const skip: TakeSkip = { copy_id: num(x.copy_id), reason: str(x.reason) }
      if (x.current_qty !== undefined && x.current_qty !== null) skip.current_qty = num(x.current_qty)
      return skip
    }),
    applied: arr(o.applied).map(a => {
      const x = obj(a)
      return typeof x.reason === 'string' ? { copy_id: num(x.copy_id), qty: num(x.qty), reason: x.reason } : { copy_id: num(x.copy_id), qty: num(x.qty) }
    }),
    copies_rev: num(o.copies_rev),
    names_changed: o.names_changed === undefined ? true : bool(o.names_changed),
  }
  if (typeof o.undo_token === 'string' && o.undo_token) r.undo_token = o.undo_token
  return r
}

export function decodeTakeUndoResponse(raw: unknown): TakeUndoResponse {
  const o = obj(raw)
  return {
    restored_qty: num(o.restored_qty),
    restored: arr(o.restored).map(a => { const x = obj(a); return { copy_id: num(x.copy_id), qty: num(x.qty), result_copy_id: num(x.result_copy_id) } }),
    copies_rev: num(o.copies_rev),
    names_changed: o.names_changed === undefined ? true : bool(o.names_changed),
  }
}

/** A 409 undo_conflict's per-line reasons (nothing was restored, the token stays). */
export function undoConflicts(e: unknown): { copy_id: number; reason: string }[] {
  if (!isCopiesError(e) || !Array.isArray(e.body?.conflicts)) return []
  return (e.body.conflicts as unknown[]).flatMap(c => {
    const o = c && typeof c === 'object' ? c as Record<string, unknown> : null
    return o && typeof o.copy_id === 'number' && typeof o.reason === 'string' ? [{ copy_id: o.copy_id, reason: o.reason }] : []
  })
}

/** A money value as a decimal string: strings kept, numbers stringified, else null. */
export function decimalOrNull(v: unknown): string | null {
  if (v === null || v === undefined) return null
  if (typeof v === 'number' && Number.isFinite(v)) return String(v)
  if (typeof v === 'string') return v
  return fail()
}

export function decodeValueHistory(raw: unknown): ValueHistory {
  const o = obj(raw)
  const c = obj(o.current)
  const change = o.change === null || o.change === undefined ? null : obj(o.change)
  return {
    // Absent from a server that predates sealed product: empty / null.
    sealed_points: Array.isArray(o.sealed_points)
      ? o.sealed_points.map(p => {
        const x = obj(p)
        return { day: str(x.day), value_usd: decimalOrNull(x.value_usd), unpriced: typeof x.unpriced === 'number' ? x.unpriced : 0 }
      })
      : [],
    basis_changed_on: strOrNull(o.basis_changed_on),
    points: arr(o.points).map(p => {
      const x = obj(p)
      return { date: str(x.date), value_usd: num(x.value_usd), cost_basis_usd: numOrNull(x.cost_basis_usd) }
    }),
    current: {
      value_usd: num(c.value_usd),
      cost_basis_usd: numOrNull(c.cost_basis_usd),
      gain_usd: numOrNull(c.gain_usd),
      as_of: strOrNull(c.as_of),
      sealed_value_usd: decimalOrNull(c.sealed_value_usd),
    },
    change: change ? { abs_usd: num(change.abs_usd), pct: numOrNull(change.pct) } : null,
    first_date: strOrNull(o.first_date),
  }
}

/** `missing_cards[].printing`: the API sends only {set, collector_number}. */
function decodePrintingRef(raw: unknown): PrintingRef {
  const x = obj(raw)
  return { set: str(x.set), collector_number: str(x.collector_number) }
}

/** `missing_cards[].printings[]`: per named printing, with its quantities. */
function decodeMissingPrinting(raw: unknown): MissingPrinting {
  const x = obj(raw)
  return {
    set: str(x.set),
    collector_number: str(x.collector_number),
    qty_needed: num(x.qty_needed),
    qty_have: num(x.qty_have),
  }
}

export function decodeDeckOwnership(raw: unknown): DeckOwnership {
  const o = obj(raw)
  return {
    deck_id: strOrNull(o.deck_id),
    name: strOrNull(o.name),
    commander: strOrNull(o.commander),
    card_count: num(o.card_count),
    have: num(o.have),
    have_proxy: num(o.have_proxy),
    missing: num(o.missing),
    missing_cards: arr(o.missing_cards).map(m => {
      const x = obj(m)
      return {
        name: str(x.name),
        qty_needed: num(x.qty_needed),
        qty_have: num(x.qty_have),
        printing: x.printing === null || x.printing === undefined ? null : decodePrintingRef(x.printing),
        printings: Array.isArray(x.printings) ? x.printings.map(decodeMissingPrinting) : [],
        have_other_printing: x.have_other_printing === true,
        qty_wanted: x.qty_wanted === undefined ? null : numOrNull(x.qty_wanted),
      }
    }),
    other_printing_cards: o.other_printing_cards === undefined || o.other_printing_cards === null
      ? []
      : arr(o.other_printing_cards).map(decodeOtherPrintingCard),
    proxy_cards: o.proxy_cards === undefined || o.proxy_cards === null
      ? []
      : arr(o.proxy_cards).map(decodeProxyCard),
  }
}

function decodeOtherPrintingCard(raw: unknown): OtherPrintingCard {
  const x = obj(raw)
  const dp = obj(x.deck_printing)
  return {
    name: str(x.name),
    deck_printing: { set: str(dp.set), collector_number: str(dp.collector_number) },
    owned_printings: Array.isArray(x.owned_printings)
      ? x.owned_printings.map(r => {
        const y = obj(r)
        return {
          set: str(y.set),
          collector_number: str(y.collector_number),
          finish: str(y.finish),
          proxy: y.proxy === true,
          qty: num(y.qty),
        }
      })
      : [],
    qty_needed: num(x.qty_needed),
  }
}

function decodeProxyCard(raw: unknown): ProxyCard {
  const x = obj(raw)
  return {
    name: str(x.name),
    qty_needed: num(x.qty_needed),
    qty_have: num(x.qty_have),
    qty_proxy: num(x.qty_proxy),
    unit_price_usd: numOrNull(x.unit_price_usd),
  }
}

export function decodeDeckLinks(raw: unknown): DeckLinks {
  const o = obj(raw)
  const cardsRaw = obj(o.cards)
  const cards: DeckLinks['cards'] = {}
  for (const [k, v] of Object.entries(cardsRaw)) {
    const x = obj(v)
    cards[k] = { deck_count: num(x.deck_count), deck_ids: arr(x.deck_ids).map(str) }
  }
  return {
    decks: arr(o.decks).map(decodeDeckOwnership),
    cards,
    decks_read: num(o.decks_read),
    decks_failed: bool(o.decks_failed),
    decks_truncated: bool(o.decks_truncated),
    decks_skipped: typeof o.decks_skipped === 'number' ? o.decks_skipped : 0,
  }
}

export async function responseJson(res: Response): Promise<unknown> {
  return json(res)
}

async function json(res: Response): Promise<unknown> {
  try {
    return await res.json()
  } catch {
    throw new CopiesError(ERR.decode)
  }
}

// --- Filter canonicalization (spec 3.1b "Filter hash") ------------------------
//
// ONE definition of "the same filters": the query key, the request body and
// the cursor's filter hash all derive from this, so a cursor is only ever
// passed back with the identical filters it was issued for. Pinned against
// the API's own hash examples in __fixtures__/copies_wire/filter_hash.json.

const CONDITION_ALIASES: Record<string, CopyCondition> = {
  nm: 'NM', near_mint: 'NM', 'near mint': 'NM', mint: 'NM', m: 'NM',
  lp: 'LP', lightly_played: 'LP', 'lightly played': 'LP', excellent: 'LP', ex: 'LP',
  mp: 'MP', moderately_played: 'MP', 'moderately played': 'MP', played: 'MP', good: 'MP',
  hp: 'HP', heavily_played: 'HP', 'heavily played': 'HP',
  dmg: 'DMG', damaged: 'DMG', poor: 'DMG',
}

const LANGUAGE_ALIASES: Record<string, string> = {
  english: 'en', spanish: 'es', french: 'fr', german: 'de', italian: 'it', portuguese: 'pt',
  japanese: 'ja', korean: 'ko', russian: 'ru', 'chinese simplified': 'zhs', 'simplified chinese': 'zhs',
  'chinese traditional': 'zht', 'traditional chinese': 'zht', phyrexian: 'ph',
}

const TYPE_BY_LOWER: Record<string, string> = Object.fromEntries(TYPE_CATEGORIES.map(t => [t.toLowerCase(), t]))
const COLOR_SET = new Set<string>(COLOR_CODES)
const CONDITION_SET = new Set<string>(COPY_CONDITIONS)

function canonArray(v: string[] | null | undefined, map: (s: string) => string | null): string[] {
  if (!v) return []
  const out = new Set<string>()
  for (const raw of v) {
    if (typeof raw !== 'string') continue
    const m = map(raw.trim())
    if (m) out.add(m)
  }
  return [...out].sort()
}

/** A mana value bound as the server writes it: decimal, no trailing zeros. */
export function canonMv(v: number | null | undefined): string | null {
  if (v === null || v === undefined || !Number.isFinite(v)) return null
  return String(Number(v))
}

const PRICE_KEYS = ['price_min', 'price_max'] as const

export function canonCondition(s: string): string | null {
  if (CONDITION_SET.has(s.toUpperCase())) return s.toUpperCase()
  return CONDITION_ALIASES[s.toLowerCase()] ?? (s ? s : null)
}

export function canonLanguage(s: string): string | null {
  const lower = s.toLowerCase()
  return LANGUAGE_ALIASES[lower] ?? (lower || null)
}

/** A price bound as the server writes it ("30" -> "30.00"); null when absent or unreadable. */
export function canonPrice(v: string | number | null | undefined): string | null {
  if (v === null || v === undefined) return null
  const text = typeof v === 'number' ? (Number.isFinite(v) ? String(v) : '') : v.trim()
  if (!/^\d{1,7}(\.\d{1,2})?$/.test(text)) return null
  const [whole, frac = ''] = text.split('.')
  return `${String(Number(whole))}.${(frac + '00').slice(0, 2)}`
}

/** An added bound as an ISO date; null when absent or not YYYY-MM-DD. */
export function canonDay(v: string | null | undefined): string | null {
  if (typeof v !== 'string') return null
  const t = v.trim()
  if (!/^\d{4}-\d{2}-\d{2}$/.test(t)) return null
  const [y, m, day] = t.split('-').map(Number)
  const d = new Date(Date.UTC(y, m - 1, day))
  // Rejects 2026-02-30 and the like (the date would roll over).
  return d.getUTCFullYear() === y && d.getUTCMonth() === m - 1 && d.getUTCDate() === day ? t : null
}

function canonBinder(v: BinderRef | null | undefined): BinderRef | null {
  if (v === UNSORTED) return UNSORTED
  return typeof v === 'number' && Number.isInteger(v) ? v : null
}

function canonColorMode(mode: ColorMode | null | undefined): ColorMode {
  return mode === COLOR_MODE.fits || mode === COLOR_MODE.exact ? mode : COLOR_MODE.any
}

/**
 * The canonical filter object. `group` is the search's top-level key, not a
 * filter, but it joins the hashed object when it is not `none` (group spec
 * 4), so a grouped and a per-line search never share a cursor or a cache entry.
 */
export function canonicalizeFilters(f: CopiesFilters = {}, group: GroupMode = GROUP_MODE.none): CanonicalFilters {
  const q = typeof f.q === 'string' ? f.q.trim().toLowerCase() : ''
  const color = canonArray(f.color, s => (COLOR_SET.has(s.toUpperCase()) ? s.toUpperCase() : null))
  const out: CanonicalFilters = {
    q: q || null,
    status: canonArray(f.status, s => s.toLowerCase() || null),
    set: canonArray(f.set, s => s.toLowerCase() || null),
    finish: canonArray(f.finish, s => s.toLowerCase() || null),
    condition: canonArray(f.condition, canonCondition),
    language: canonArray(f.language, canonLanguage),
    color,
    color_mode: color.length > 0 ? canonColorMode(f.color_mode) : COLOR_MODE.any,
    type: canonArray(f.type, s => TYPE_BY_LOWER[s.toLowerCase()] ?? null),
    rarity: canonArray(f.rarity, s => s.toLowerCase() || null),
    mv_min: canonMv(f.mv_min),
    mv_max: canonMv(f.mv_max),
    name_keys: canonArray(f.name_keys, s => s.toLowerCase() || null),
    price_min: canonPrice(f.price_min),
    price_max: canonPrice(f.price_max),
    added_from: canonDay(f.added_from),
    added_to: canonDay(f.added_to),
    binder: canonBinder(f.binder),
  }
  const syntax = typeof f.syntax === 'string' ? f.syntax.split(/\s+/).filter(Boolean).join(' ') : ''
  if (syntax) out.syntax = syntax
  if (typeof f.qty_min === 'number' && Number.isInteger(f.qty_min)) out.qty_min = f.qty_min
  const colorGroup = canonArray(f.color_group, s => ((COLOR_GROUP_KEYS as readonly string[]).includes(s.toUpperCase()) ? s.toUpperCase() : null))
  if (colorGroup.length) out.color_group = colorGroup
  const typeGroup = canonArray(f.type_group, s => TYPE_BY_LOWER[s.toLowerCase()] ?? null)
  if (typeGroup.length) out.type_group = typeGroup
  const location = canonBinder(f.location)
  if (location !== null) out.location = location
  if (group === GROUP_MODE.printing || group === GROUP_MODE.name) out.group = group
  return out
}

/** The canonical object as JSON with sorted keys and no whitespace (the hash input). */
export function canonicalFilterJson(c: CanonicalFilters): string {
  const keys = Object.keys(c).sort() as (keyof CanonicalFilters)[]
  return `{${keys.map(k => `${JSON.stringify(k)}:${JSON.stringify(c[k])}`).join(',')}}`
}

/** The `filters` object of a search body: only the keys that filter. */
export function filtersBody(c: CanonicalFilters): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  if (c.q) out.q = c.q
  for (const k of ARRAY_FILTER_KEYS) {
    if (c[k].length) out[k] = c[k]
  }
  if (c.color.length) {
    out.color = c.color
    out.color_mode = c.color_mode
  }
  if (c.mv_min !== null) out.mv_min = Number(c.mv_min)
  if (c.mv_max !== null) out.mv_max = Number(c.mv_max)
  if (c.syntax) out.syntax = c.syntax
  // Decimal strings and ISO dates, as the server takes them.
  if (c.price_min !== null) out.price_min = c.price_min
  if (c.price_max !== null) out.price_max = c.price_max
  if (c.added_from !== null) out.added_from = c.added_from
  if (c.added_to !== null) out.added_to = c.added_to
  if (c.qty_min !== undefined) out.qty_min = c.qty_min
  if (c.binder !== null) out.binder = c.binder
  if (c.color_group?.length) out.color_group = c.color_group
  if (c.type_group?.length) out.type_group = c.type_group
  if (c.location !== undefined) out.location = c.location
  return out
}

export function buildSearchBody(req: SearchRequest): Record<string, unknown> {
  const filters = filtersBody(canonicalizeFilters(req.filters))
  // A valid price STRING goes out exactly as the caller wrote it ("5", not
  // "5.00"); the canonical 2-place form is what the hash and query key use.
  for (const k of PRICE_KEYS) {
    const raw = req.filters?.[k]
    if (filters[k] !== undefined && typeof raw === 'string') filters[k] = raw.trim()
  }
  const body: Record<string, unknown> = { filters }
  if (req.sort) body.sort = req.sort
  if (req.dir) body.dir = req.dir
  if (req.cursor) body.cursor = req.cursor
  if (req.limit !== undefined) body.limit = req.limit
  if (req.facets) body.facets = true
  // `none` is the server's default: never sent, so a per-line body is byte for byte today's.
  if (req.group === GROUP_MODE.printing || req.group === GROUP_MODE.name) body.group = req.group
  if (req.include?.length) body.include = [...req.include]
  return body
}

/** One POST /copies/groups request (collection views API). */
export interface GroupsRequest {
  by: SectionBy
  filters?: CopiesFilters
  sort?: CopySort
  dir?: SortDir
  /** Lines per group, in the request's sort (0..24). */
  preview?: number
  /** Groups per page (1..40). */
  groupLimit?: number
  cursor?: string | null
  include?: readonly CopiesInclude[]
}

export function buildGroupsBody(req: GroupsRequest): Record<string, unknown> {
  const body = buildSearchBody({ filters: req.filters, sort: req.sort, dir: req.dir, cursor: req.cursor, include: req.include })
  body.by = req.by
  if (req.preview !== undefined) body.preview = Math.max(0, Math.min(GROUPS.previewMax, Math.floor(req.preview)))
  if (req.groupLimit !== undefined) body.group_limit = Math.max(1, Math.min(40, Math.floor(req.groupLimit)))
  return body
}

/** The lines a groups page counts under no key (a set grouping's name-only lines): an "Other" section, never drilled. */
export interface GroupTotals {
  copy_qty: number
  line_count: number
  value_usd: number | null
  unpriced_qty: number
}

/** One group of POST /copies/groups. */
export interface CopiesSection extends GroupTotals {
  key: string
  label: string
  /** Up to 3 image_normal, de-duplicated by printing, priciest first. */
  top_images: string[]
  /** The /copies/search filters that open exactly this group (the request's filters plus the key). */
  filter: CopiesFilters
  /** Up to `preview` lines in the request's sort. */
  preview: CopyItem[]
}

export interface CopiesSectionsPage {
  by: string
  groups: CopiesSection[]
  next_cursor: string | null
  total_groups: number | null
  ungrouped: GroupTotals | null
  copies_rev: number | null
}

function totalsOf(o: Obj): GroupTotals {
  const n = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : 0)
  return {
    copy_qty: n(o.copy_qty),
    line_count: n(o.line_count),
    value_usd: typeof o.value_usd === 'number' && Number.isFinite(o.value_usd) ? o.value_usd
      : typeof o.value_usd === 'string' && Number.isFinite(Number(o.value_usd)) ? Number(o.value_usd) : null,
    unpriced_qty: n(o.unpriced_qty),
  }
}

/** A group's `filter`: a plain object, kept as the server sent it (it is sent back verbatim). */
function filterOf(v: unknown): CopiesFilters {
  return obj(v) as CopiesFilters
}

export function decodeCopiesSection(raw: unknown): CopiesSection {
  const o = obj(raw)
  return {
    key: str(o.key),
    label: typeof o.label === 'string' ? o.label : str(o.key),
    ...totalsOf(o),
    top_images: Array.isArray(o.top_images) ? o.top_images.filter((u): u is string => typeof u === 'string').slice(0, 3) : [],
    filter: filterOf(o.filter),
    preview: Array.isArray(o.preview) ? o.preview.map(decodeCopyItem) : [],
  }
}

export function decodeCopiesSectionsPage(raw: unknown): CopiesSectionsPage {
  const o = obj(raw)
  const page: CopiesSectionsPage = {
    by: typeof o.by === 'string' ? o.by : '',
    groups: arr(o.groups).map(decodeCopiesSection),
    next_cursor: strOrNull(o.next_cursor),
    total_groups: typeof o.total_groups === 'number' ? o.total_groups : null,
    ungrouped: o.ungrouped && typeof o.ungrouped === 'object' ? totalsOf(o.ungrouped as Obj) : null,
    copies_rev: typeof o.copies_rev === 'number' ? o.copies_rev : null,
  }
  for (const g of page.groups) for (const it of g.preview) stampLineRev(it, page.copies_rev)
  return page
}

/** The 422 of a later groups page whose cursor no longer fits (an edit or a price refresh). */
export function isGroupsBadCursor(e: unknown): boolean {
  return isCopiesError(e) && e.kind === ERR.validation && (e.detail === DETAIL.groupsBadCursor || e.detail === DETAIL.badCursor)
}

// --- Endpoints ----------------------------------------------------------------

/** The capability probe: `GET /copies?limit=1`. 200 = per_copy. */
export async function probeCopies(ctx: CopiesContext): Promise<CopiesPage> {
  const res = await copiesFetch(ctx, { method: HTTP.get, sub: SEG.copies, query: { limit: 1 }, probe: true })
  return decodeCopiesPage(await json(res))
}

export interface ListQuery {
  cursor?: string | null
  limit?: number
  q?: string
  status?: CopyStatus[]
  set?: string
  finish?: CopyFinish
  condition?: string
  language?: string
  sort?: CopySort
  dir?: SortDir
}

/** `GET /copies` (spec 3.1). */
export async function listCopies(ctx: CopiesContext, q: ListQuery = {}): Promise<CopiesPage> {
  const res = await copiesFetch(ctx, {
    method: HTTP.get,
    sub: SEG.copies,
    query: {
      cursor: q.cursor ?? undefined,
      limit: q.limit,
      q: q.q,
      status: q.status?.length ? q.status.join(',') : undefined,
      set: q.set,
      finish: q.finish,
      condition: q.condition,
      language: q.language,
      sort: q.sort,
      dir: q.dir,
    },
  })
  return decodeCopiesPage(await json(res))
}

/** `POST /copies/search` (spec 3.1b). */
export async function searchCopies(ctx: CopiesContext, req: SearchRequest): Promise<CopiesPage> {
  const res = await copiesFetch(ctx, { method: HTTP.post, sub: SEG.search, body: buildSearchBody(req) })
  return decodeCopiesPage(await json(res))
}

/** `POST /copies/search` with `group` printing | name (group spec 1). */
export async function searchCopyGroups(
  ctx: CopiesContext, req: SearchRequest & { group: Exclude<GroupMode, 'none'> },
): Promise<CopiesGroupPage> {
  const res = await copiesFetch(ctx, { method: HTTP.post, sub: SEG.search, body: buildSearchBody(req) })
  return decodeCopiesGroupPage(await json(res))
}

/** `POST /copies/groups` (collection views API): one page of groups with totals and previews. */
export async function fetchCopiesSections(ctx: CopiesContext, req: GroupsRequest): Promise<CopiesSectionsPage> {
  const res = await copiesFetch(ctx, { method: HTTP.post, sub: SEG.groups, body: buildGroupsBody(req) })
  return decodeCopiesSectionsPage(await json(res))
}

/** `POST /copies` (spec 3.2). Partial success is a 200 with `errors`. */
export async function addCopies(
  ctx: CopiesContext, items: AddItem[], opts: { source?: AddSource; opKey?: string } = {},
): Promise<AddResponse> {
  const body: Record<string, unknown> = { items }
  if (opts.source) body.source = opts.source
  if (opts.opKey) body.op_key = opts.opKey
  const res = await copiesFetch(ctx, { method: HTTP.post, sub: SEG.copies, body })
  return decodeAddResponse(await json(res))
}

/** `PATCH /copies/{copy_id}` (spec 3.3). */
export async function patchCopy(ctx: CopiesContext, copyId: number, body: PatchBody): Promise<PatchResponse> {
  const res = await copiesFetch(ctx, { method: HTTP.patch, sub: `${SEG.copies}/${copyId}`, body })
  return decodePatchResponse(await json(res))
}

/** `DELETE /copies/{copy_id}` (spec 3.4): 204, idempotent, no op_key. */
export async function deleteCopy(ctx: CopiesContext, copyId: number): Promise<void> {
  await copiesFetch(ctx, { method: HTTP.delete, sub: `${SEG.copies}/${copyId}` })
}

/** `POST /copies/bulk` (spec 3.5). */
export async function bulkCopies(
  ctx: CopiesContext, req: { copyIds: number[]; action: BulkAction; value?: BulkValue; opKey?: string },
): Promise<BulkResponse> {
  const body: Record<string, unknown> = { copy_ids: req.copyIds, action: req.action }
  if (req.value !== undefined) body.value = req.value
  if (req.opKey) body.op_key = req.opKey
  const res = await copiesFetch(ctx, { method: HTTP.post, sub: SEG.bulk, body })
  return decodeBulkResponse(await json(res))
}

/**
 * `POST /copies/ids` (select all matching): every line the filters match,
 * or `too_many`. `filters` is the search body's filters object
 * (filtersBody(canonicalizeFilters(...))), so the match set is the search's.
 */
export async function copiesIds(ctx: CopiesContext, filters: Record<string, unknown>): Promise<CopyIds> {
  const res = await copiesFetch(ctx, { method: HTTP.post, sub: SEG.ids, body: { filters } })
  return decodeCopyIds(await json(res))
}

/** `POST /copies/match-list`: a pasted list (text or CSV) against the owned lines. Read-only. */
export async function matchCopyList(ctx: CopiesContext, text: string): Promise<MatchList> {
  const res = await copiesFetch(ctx, { method: HTTP.post, sub: SEG.matchList, body: { text } })
  return decodeMatchList(await json(res))
}

/**
 * `POST /copies/take`: remove or move an exact qty per line. `items` must
 * name each copy_id once (aggregateTake); `binderId` is for move only (null
 * = Unsorted). The op_key is required.
 */
export async function takeCopies(
  ctx: CopiesContext, req: { items: TakeItem[]; action: TakeAction; binderId?: number | null; opKey: string },
): Promise<TakeResponse> {
  const body: Record<string, unknown> = { items: req.items, action: req.action, op_key: req.opKey }
  if (req.action === 'move') body.binder_id = req.binderId ?? null
  const res = await copiesFetch(ctx, { method: HTTP.post, sub: SEG.take, body })
  return decodeTakeResponse(await json(res), req.action)
}

/** `POST /copies/take/undo`: put back exactly what one remove took. */
export async function undoTake(ctx: CopiesContext, req: { token: string; opKey: string }): Promise<TakeUndoResponse> {
  const res = await copiesFetch(ctx, { method: HTTP.post, sub: SEG.takeUndo, body: { token: req.token, op_key: req.opKey } })
  return decodeTakeUndoResponse(await json(res))
}

/** The filename from a Content-Disposition header, or null. */
export function filenameFromDisposition(header: string | null): string | null {
  if (!header) return null
  const m = /filename="([^"]+)"/i.exec(header) ?? /filename=([^;]+)/i.exec(header)
  return m ? m[1].trim() : null
}

/** `GET /export` (spec 3.6), as a Blob: the bearer is required, so no plain link. */
export async function exportCollection(
  ctx: CopiesContext, format: ExportFormat, status?: CopyStatus[],
): Promise<ExportResult> {
  const res = await copiesFetch(ctx, {
    method: HTTP.get,
    sub: SEG.export,
    query: { format, status: status?.length ? status.join(',') : undefined },
  })
  return { blob: await res.blob(), filename: filenameFromDisposition(res.headers.get(HTTP.contentDisposition)) }
}

/** `GET /value/history` (spec 3.7). */
export async function fetchValueHistory(ctx: CopiesContext, range: ValueRange): Promise<ValueHistory> {
  const res = await copiesFetch(ctx, { method: HTTP.get, sub: SEG.valueHistory, query: { range } })
  return decodeValueHistory(await json(res))
}

/** `GET /deck-links` (spec 3.8). */
export async function fetchDeckLinks(ctx: CopiesContext): Promise<DeckLinks> {
  const res = await copiesFetch(ctx, { method: HTTP.get, sub: SEG.deckLinks })
  return decodeDeckLinks(await json(res))
}

/** `POST /deck-ownership` (spec 3.9). */
export async function fetchDeckOwnership(ctx: CopiesContext, decklist: string): Promise<DeckOwnership> {
  const res = await copiesFetch(ctx, { method: HTTP.post, sub: SEG.deckOwnership, body: { decklist } })
  return decodeDeckOwnership(await json(res))
}

/** A fresh op_key: 32 hex characters (16-64 of [A-Za-z0-9_-]). */
export function newOpKey(): string {
  const c = globalThis.crypto
  if (c?.randomUUID) return c.randomUUID().replace(/-/g, '')
  let s = ''
  for (let i = 0; i < 32; i++) s += Math.floor(Math.random() * 16).toString(16)
  return s
}
